import React from 'react';
import PropTypes from 'prop-types';
import { JourneyContext } from './context';
import { useResolvedJourney } from './useResolvedJourney';

/**
 * Resolves the active journey and provides it to the shared shell subtree.
 *
 * The default journey is the eager core, so it renders immediately. A non-default
 * journey is lazy-loaded (its own chunk); until it resolves the shell runs on the
 * default journey, then swaps in the loaded one — no blocking Suspense boundary, and
 * shared state in the shell above/around this provider survives the swap.
 *
 * Mounted at the top of the shared shell (AssistantShell, wrapping DrawerProvider),
 * NOT inside AssistantOrchestrator: message renderers import useAssistant, so providing
 * journeys from the provider would create a provider → journeys → registry → renderer →
 * provider import cycle. The shell root is outside the renderer import chain, and the
 * journey chunk is a *dynamic* import, so there is no static cycle.
 */
export const JourneyProvider = ({ journeyId = 'assistant', children }) => {
  // Render side: serve `journey` immediately and ignore `isResolving` — the default
  // journey renders while a lazy one loads, then swaps in. The resolution itself lives
  // in useResolvedJourney (Seq F.c.0), shared with AssistantDataProvider above the
  // orchestrator, which applies the opposite (holding) policy for the reason documented
  // there. The dynamic import is module-cached, so two callers load the chunk once.
  const { journey } = useResolvedJourney(journeyId);

  return (
    <JourneyContext.Provider value={journey}>
      {children}
    </JourneyContext.Provider>
  );
};

JourneyProvider.propTypes = {
  journeyId: PropTypes.string,
  children: PropTypes.node.isRequired,
};
